import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { notDesktop } from '@zola-helpers/client';
import { trackEvent } from 'util/trackingHelper';
import { SETTINGS_BY_CARD_TYPE } from 'cards/constants/CardTypes';
import { cloneSampleProject as cloneSampleProjectAction } from '../../../../actions/projectActions';
import * as actions from '../../../../reducers/pdp/actions';
import * as selectors from '../../../../reducers/pdp/selectors';
import { getPricing } from '../../../../reducers/pricing/selectors';
import { AllOptionsSection } from '../../../CardDetail/AllOptionsSection';
import Loader from '../../../../../components/common/ui/Loader';
import { QtyPriceSection } from '../../../CardDetail/DetailQtyPriced';
import { getStepUrl } from '../../../../util/getStepUrl';

import styles from './styles.module.less';

const ProjectCloneModal = ({
  activeVariationUUID,
  activeUUID,
  activeMediumSelection,
  images,
  cloneSampleProject,
  cost,
  fetchProjectCardSuiteDetails,
  hideModalFn,
  isBusy,
  pageData,
  project: { project_uuid: projectUUID },
  quantity,
  reopenCurrentModal,
  setActiveCardSuite,
  setQuantity,
  setOptionValue,
  medium,
  sectionOrigin,
  currentProjectUUID,
}) => {
  const [isCloningProject, setIsCloningProject] = useState(false);
  const [initializedQtyToMin, setInitializedQtyToMin] = useState(false);
  const isMobileOrTablet = notDesktop();

  useEffect(() => {
    // Don't refetch the project's card suite details if they're already loaded
    if (projectUUID !== currentProjectUUID) {
      fetchProjectCardSuiteDetails(projectUUID).then(cardSuiteUUID => {
        if (cardSuiteUUID) {
          setActiveCardSuite(cardSuiteUUID);
          if (medium) {
            // The 'type' attribute of the card variation options refers to the card medium, not the card type in the PDP options
            // We set the 'type' option value to the medium of the reference card of the component initiating this modal
            setOptionValue({
              type: 'type',
              value: medium.toLowerCase(),
            });
          }
        }
      });
    }
  }, [projectUUID, currentProjectUUID]);

  const handleCustomizeClick = useCallback(() => {
    setIsCloningProject(true);
    cloneSampleProject(projectUUID, activeVariationUUID, quantity)
      .then(newProject => {
        const leadCustomization = newProject.customizations.find(
          customization => customization.lead
        );

        const trackingData = {
          customization_type: leadCustomization?.type,
          customization_medium: leadCustomization?.medium,
          customization_size: leadCustomization?.variation?.option_values?.size,
          card_project_customization_id: newProject.uuid,
          location: 'ORDERS_DRAFTS',
          section: sectionOrigin || 'ORDERS',
          referrer_card_project_uuid: projectUUID,
        };
        trackEvent('Customization Started', trackingData);

        browserHistory.push(getStepUrl(newProject.uuid));
        hideModalFn();
      })
      .catch(() => {
        setIsCloningProject(false);
      });
  }, [
    activeVariationUUID,
    cloneSampleProject,
    hideModalFn,
    projectUUID,
    quantity,
    setIsCloningProject,
  ]);

  // When users clone a sample project, we don't want to use the default quantity (1) from the BE.
  // Instead, we reset the quantity to the minimum "non-sample" amount (typically 20 or 50).
  useEffect(() => {
    // Only reset the quantity once
    if (!initializedQtyToMin && pageData && pageData.paperInfo) {
      setInitializedQtyToMin(true);
      setQuantity(pageData.paperInfo.qty_min);
    }
  }, [pageData, initializedQtyToMin, setQuantity]);

  /*
   * Trigger a tracking event named `Product Viewed`
   * A project clone modal is displaying the right panel of a card PDP
   * It acts as if the user went back to a specific card PDP altogether
   * Therefore, in terms of tracking, it acts as if the PDP was visited
   */
  useEffect(() => {
    /*
     * Previous loaded data is stored and data update is asynch
     * For the tracking event to only be fired once when switching order,
     * check for equivalence between existing and updated data first.
     */
    if (pageData?.uuid === activeUUID && currentProjectUUID === projectUUID) {
      const trackingData = {
        product_id: pageData.uuid,
        sku: activeVariationUUID,
        category: 'Paper',
        name: pageData.name,
        price: cost.total,
        quantity: pageData.paperInfo.qty_min,
        url: `/wedding-planning/${SETTINGS_BY_CARD_TYPE[pageData.cardType].urlSlug}/${activeUUID}`,
        image_url: images[activeMediumSelection.color]?.thumbnail,
        location: 'ORDERS_DRAFTS',
        section: sectionOrigin || 'ORDERS',
        medium: activeMediumSelection.type,
        size: activeMediumSelection.size,
        referrer_card_project_uuid: projectUUID,
      };
      trackEvent('Product Viewed', trackingData);
    }
  }, [activeUUID, pageData, currentProjectUUID, projectUUID]);

  if (isBusy) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader />
      </div>
    );
  }

  const { paperInfo } = pageData;

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.drawerBody}>
        <div className={styles.drawerTitle}>More Ways to Customize Your Design</div>
        <div className={styles.drawerSubtitle}>Make your cards as unique as you two.</div>
        <AllOptionsSection
          isAdminView={false}
          location="ORDER_MORE_MODAL"
          paperTypeModalProps={{ hideProofBanner: true }}
          paperTypeModalOptions={{
            onBack: reopenCurrentModal,
            hideClose: true,
            disableBackgroundClick: true,
            size: 'lg',
            showInDrawer: true,
          }}
        />
      </div>
      <div className={styles.drawerButtons}>
        <QtyPriceSection
          proofAllowed={paperInfo.qty_single_allowed}
          cost={cost}
          defaultSelection={paperInfo.qty_min}
          increment={paperInfo.qty_increment}
          max={paperInfo.qty_max}
          min={paperInfo.qty_min}
          onSelect={setQuantity}
          hideTitle={isMobileOrTablet}
          handleCustomizeClick={handleCustomizeClick}
          disableCustomizeButton={!activeVariationUUID || isCloningProject}
          showDropdownOptionsOnTop
        />
      </div>
    </div>
  );
};

const mapsStateToProps = state => ({
  activeVariationUUID: selectors.getActiveVariationUUID(state),
  activeUUID: selectors.getActiveUUID(state),
  activeMediumSelection: selectors.getActiveSelection(state),
  currentProjectUUID: selectors.getProjectUUID(state),
  images: selectors.getImages(state),
  cost: selectors.getCostData(state),
  isBusy: selectors.getIsBusy(state),
  pageData: selectors.getPageData(state),
  pricing: getPricing(state),
  quantity: selectors.getQuantity(state),
});

const mapDispatchToProps = {
  fetchProjectCardSuiteDetails: actions.fetchCardSuiteByProjectId,
  setActiveCardSuite: actions.setActiveSuite,
  setQuantity: actions.setQuantity,
  setOptionValue: actions.setOptionValue,
  cloneSampleProject: cloneSampleProjectAction,
};

export default {
  component: connect(mapsStateToProps, mapDispatchToProps)(ProjectCloneModal),
  options: { size: 'md', showInDrawer: true },
};
